import { CustomActionDto, TrainingSelectionStrategy } from '@ampsec/platform-client';
import { AMPLIFIER, CUSTOM_ACTION } from 'Core-utils/constants/constants';
import {
    AmplifierTrainingTemplateCopyFromI18n,
    AmplifierTrainingTemplateKey,
} from 'Core-utils/i18n/amplifierTrainingTemplateI18n';

export type { AmplifierTrainingTemplateKey } from 'Core-utils/i18n/amplifierTrainingTemplateI18n';

export const AMPLIFIER_TRAINING_TEMPLATE_PREFIX = 'amplifier-ct-';

/** Synthetic id for the top-level Amplifier tile in the actions picker (opens Amplifier filter). */
export const AMPLIFIER_GROUP_CARD_ID = 'amplifier-group';

export interface AmplifierTrainingTemplateDefinition {
    id: string;
    key: AmplifierTrainingTemplateKey;
    /** Card header and wizard context title */
    title: string;
    /** Short label under logo on picker cards */
    cardSubtitle: string;
    description: string;
    actionNamePlaceholder: string;
    actionNameHelper: string;
    descriptionPlaceholder?: string;
    selectionStrategy: TrainingSelectionStrategy;
    /** Minimum modules required when this template uses module selection */
    minModules?: number;
}

type AmplifierTrainingTemplateCore = Pick<
    AmplifierTrainingTemplateDefinition,
    'key' | 'selectionStrategy' | 'minModules'
>;

const AMPLIFIER_TRAINING_TEMPLATE_CORE: AmplifierTrainingTemplateCore[] = [
    { key: 'scheduled', selectionStrategy: TrainingSelectionStrategy.Scheduled },
    { key: 'module', selectionStrategy: TrainingSelectionStrategy.Module, minModules: 1 },
    { key: 'custom', selectionStrategy: TrainingSelectionStrategy.Custom, minModules: 2 },
];

/** Fallback copy when Contentful is unavailable or entry is missing. */
const DEFAULT_AMPLIFIER_TRAINING_TEMPLATE_COPY: Record<
    AmplifierTrainingTemplateKey,
    Omit<AmplifierTrainingTemplateCopyFromI18n, 'key'>
> = {
    scheduled: {
        title: 'Scheduled compliance training',
        cardSubtitle: 'Tenant schedule',
        description: 'Assign training using the tenant compliance schedule from Compliance Training settings.',
        actionNamePlaceholder: 'e.g. Scheduled compliance training',
        actionNameHelper: 'This name appears when you attach the action to an engagement flow.',
        descriptionPlaceholder: 'Optional notes for admins configuring this action.',
    },
    module: {
        title: 'Single module',
        cardSubtitle: 'One training module',
        description: 'Assign one compliance training module to the engaged user.',
        actionNamePlaceholder: 'e.g. Assign phishing training',
        actionNameHelper: 'Use a name that reflects the module you will select on the next step.',
        descriptionPlaceholder: 'Optional notes for admins configuring this action.',
    },
    custom: {
        title: 'Custom module set',
        cardSubtitle: 'Multiple modules',
        description: 'Assign two or more compliance training modules in one action.',
        actionNamePlaceholder: 'e.g. Phishing + device security bundle',
        actionNameHelper: 'Name the bundle so operators know which modules this action assigns.',
        descriptionPlaceholder: 'Optional notes listing who should use this bundle.',
    },
};

export const mergeAmplifierTrainingTemplates = (
    copyByKey: Partial<Record<AmplifierTrainingTemplateKey, AmplifierTrainingTemplateCopyFromI18n>> = {},
): AmplifierTrainingTemplateDefinition[] =>
    AMPLIFIER_TRAINING_TEMPLATE_CORE.map((core) => {
        const copy = copyByKey[core.key] ?? { key: core.key, ...DEFAULT_AMPLIFIER_TRAINING_TEMPLATE_COPY[core.key] };
        return {
            id: `${AMPLIFIER_TRAINING_TEMPLATE_PREFIX}${core.key}`,
            key: core.key,
            selectionStrategy: core.selectionStrategy,
            minModules: core.minModules,
            title: copy.title,
            cardSubtitle: copy.cardSubtitle,
            description: copy.description,
            actionNamePlaceholder: copy.actionNamePlaceholder,
            actionNameHelper: copy.actionNameHelper,
            descriptionPlaceholder: copy.descriptionPlaceholder,
        };
    });

/** Default templates (fallback copy). Prefer useAmplifierTrainingTemplates() in UI. */
export const DEFAULT_AMPLIFIER_TRAINING_TEMPLATES = mergeAmplifierTrainingTemplates();

/** @deprecated Use DEFAULT_AMPLIFIER_TRAINING_TEMPLATES */
export const AMPLIFIER_TRAINING_TEMPLATES = DEFAULT_AMPLIFIER_TRAINING_TEMPLATES;

export const isAmplifierTrainingTemplateId = (id?: string): boolean =>
    Boolean(id?.startsWith(AMPLIFIER_TRAINING_TEMPLATE_PREFIX));

export const getAmplifierTrainingTemplateById = (
    id?: string,
    templates: AmplifierTrainingTemplateDefinition[] = DEFAULT_AMPLIFIER_TRAINING_TEMPLATES,
): AmplifierTrainingTemplateDefinition | undefined => templates.find((template) => template.id === id);

export const getAmplifierTrainingTemplateByKey = (
    key: AmplifierTrainingTemplateKey,
    templates: AmplifierTrainingTemplateDefinition[] = DEFAULT_AMPLIFIER_TRAINING_TEMPLATES,
): AmplifierTrainingTemplateDefinition | undefined => templates.find((template) => template.key === key);

/** Blank REST action stub for the generic Custom Action template (not Amplifier training). */
export const buildGenericCustomActionStub = (): CustomActionDto => ({
    id: '',
    displayValue: CUSTOM_ACTION,
    description: 'Custom REST action',
    meta: {
        request: {
            method: '',
            url: '',
            params: {},
            headers: { temp: 'temp' },
            payload: {
                value: '',
                kind: 'TEMPLATE',
                format: 'JSON',
            },
        },
    },
    isTemplate: false,
    kind: 'REST_ACTION',
    retryStrategy: {
        kind: 'CONSTANT_BACKOFF',
        maxRetries: 0,
        delay: 0,
    },
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
});

export const buildAmplifierGroupCardStub = (): CustomActionDto => ({
    id: AMPLIFIER_GROUP_CARD_ID,
    displayValue: AMPLIFIER,
    description: 'AMPLIFIER - Compliance Training Action Templates',
    meta: {
        request: {
            method: '',
            url: '',
            params: {},
            headers: {},
            payload: {
                value: '',
                kind: 'TEMPLATE',
                format: 'JSON',
            },
        },
    },
    isTemplate: true,
    kind: 'REST_ACTION',
    retryStrategy: {
        kind: 'CONSTANT_BACKOFF',
        maxRetries: 0,
        delay: 0,
    },
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
});

export const buildAmplifierTrainingTemplateStubs = (
    templates: AmplifierTrainingTemplateDefinition[] = DEFAULT_AMPLIFIER_TRAINING_TEMPLATES,
): CustomActionDto[] =>
    templates.map((template) => ({
        id: template.id,
        displayValue: AMPLIFIER,
        description: template.description,
        meta: {
            request: {
                method: 'POST',
                url: '',
                params: {},
                headers: {},
                payload: {
                    value: '',
                    kind: 'TEMPLATE',
                    format: 'JSON',
                },
            },
        },
        isTemplate: true,
        kind: 'REST_ACTION',
        retryStrategy: {
            kind: 'CONSTANT_BACKOFF',
            maxRetries: 0,
            delay: 0,
        },
        createdAt: '',
        updatedAt: '',
        deletedAt: null,
    }));

export const TRAINING_CREATE_URL_MARKER = '/trainings/create/{{user.id}}';

export const isAmplifierTrainingRestAction = (action: CustomActionDto): boolean =>
    Boolean(action.meta?.request?.url?.includes(TRAINING_CREATE_URL_MARKER));

export const parseTrainingTemplateKeyFromAction = (action: CustomActionDto): AmplifierTrainingTemplateKey | null => {
    try {
        const payload = JSON.parse(action.meta?.request?.payload?.value ?? '{}');
        switch (payload.selectionStrategy) {
            case TrainingSelectionStrategy.Scheduled:
                return 'scheduled';
            case TrainingSelectionStrategy.Module:
                return 'module';
            case TrainingSelectionStrategy.Custom:
                return 'custom';
            default:
                return null;
        }
    } catch {
        return null;
    }
};

export const parseSelectedSubjectsFromAction = (action: CustomActionDto): string[] => {
    try {
        const payload = JSON.parse(action.meta?.request?.payload?.value ?? '{}');
        if (payload.trainingRequest?.subject) {
            return [payload.trainingRequest.subject];
        }
        if (Array.isArray(payload.trainingRequests)) {
            return payload.trainingRequests.map((request: { subject: string }) => request.subject).filter(Boolean);
        }
    } catch {
        /* ignore */
    }
    return [];
};
